/**
 * lex.ivoliq Freigabeseite (Juli 2026) — öffentlich, Token-basiert (kein Login).
 * Route: /lex/freigabe/:token · Anwalt gibt die laufende Honorarnote frei;
 * bei variablem Honorar ist der Nettobetrag anpassbar.
 * Styleguide: /app/design_guidelines_lex.json (Navy/Gold, Emerald = Status).
 */
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { LexExtrasFields, emptyExtras, extrasPayload, grossWithExtras } from "@/components/lex/LexExtrasFields";
import "@/styles/lex.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtEur = (v) =>
  v == null ? "—" : new Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR" }).format(v);
const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("de-AT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return null; }
};

const Row = ({ label, children, testId }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-slate-100" data-testid={testId}>
    <span className="text-xs uppercase tracking-widest font-semibold text-slate-400">{label}</span>
    <span className="text-sm text-slate-800 lex-mono">{children}</span>
  </div>
);

export default function LexApprove() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [amount, setAmount] = useState("");
  const [extras, setExtras] = useState(emptyExtras());
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/lex/honorar/approve/${token}`)
      .then((res) => { setInfo(res.data); setAmount(res.data.base_amount_net ?? res.data.amount_net ?? ""); setExtras(emptyExtras(res.data.extras || {})); })
      .catch((err) => setError(err?.response?.data?.detail || "Dieser Freigabe-Link ist ungültig."));
  }, [token]);

  const confirm = async () => {
    setSending(true);
    setError(null);
    try {
      const body = { extras: extrasPayload(extras), ...(info.adjustable ? { amount: parseFloat(amount) } : {}) };
      const res = await axios.post(`${API}/lex/honorar/approve/${token}`, body);
      trackEvent("lex_invoice_sent", { via: "approval_link", adjustable: Boolean(info.adjustable) });
      setDone(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Freigabe fehlgeschlagen. Bitte erneut versuchen.");
    } finally {
      setSending(false);
    }
  };

  const brutto = info ? grossWithExtras(info.adjustable ? amount : (info.base_amount_net ?? info.amount_net), extras) : null;
  const period = info?.service_date
    ? `${fmtDate(info.service_date)}${info.service_date_end ? ` – ${fmtDate(info.service_date_end)}` : ""}`
    : null;

  return (
    <div className="lex-theme min-h-screen" style={{ background: "#FDFCFB" }} data-testid="lex-approve-page">
      <div className="max-w-lg mx-auto px-6 py-14">
        <div className="text-center mb-8">
          <span className="lex-serif text-2xl font-semibold" style={{ color: "#0A1128" }}>
            lex<span style={{ color: "#C5A059" }}>.</span>ivoliq
          </span>
        </div>

        {!info && !error && (
          <p className="text-center text-sm text-slate-400" data-testid="lex-approve-loading">Lädt …</p>
        )}

        {done ? (
          <div className="lex-card p-8 text-center lex-fade-up" data-testid="lex-approve-success">
            <CheckCircle2 className="h-10 w-10 mx-auto mb-4" style={{ color: "#059669" }} />
            <h1 className="lex-serif text-3xl font-semibold mb-3" style={{ color: "#0A1128" }}>Note versendet.</h1>
            <p className="text-sm text-slate-600">
              Honorarnote <span className="lex-mono font-semibold">{done.invoice_number}</span> über{" "}
              <span className="lex-mono font-semibold">{fmtEur(done.total)}</span> ist an{" "}
              <span className="font-semibold">{done.recipient}</span> gegangen.
            </p>
            <p className="text-xs text-slate-400 mt-4">
              Sie erhalten in Kürze eine Bestätigung mit der Note als PDF · es ist nichts weiter zu tun.
            </p>
          </div>
        ) : info && info.state !== "ready" ? (
          <div className="lex-card p-8 text-center lex-fade-up" data-testid="lex-approve-invalid">
            <AlertTriangle className="h-8 w-8 mx-auto mb-4 text-slate-400" />
            <h1 className="lex-serif text-2xl font-semibold mb-3" style={{ color: "#0A1128" }}>
              {info.state === "used" ? "Bereits erledigt." : "Link abgelaufen."}
            </h1>
            <p className="text-sm text-slate-600">
              {info.state === "used"
                ? `Die Honorarnote ${info.invoice_number} wurde bereits versendet · es ist nichts weiter zu tun.`
                : `Dieser Freigabe-Link ist nicht mehr gültig. Sie können die Note ${info.invoice_number} jederzeit direkt aus der Applikation versenden.`}
            </p>
          </div>
        ) : info ? (
          <div className="lex-card p-8 lex-fade-up" data-testid="lex-approve-card">
            <div className="lex-gold-line mb-4" />
            <h1 className="lex-serif text-3xl font-semibold mb-2" style={{ color: "#0A1128" }}>
              Honorarnote freigeben.
            </h1>
            <p className="text-sm text-slate-500 mb-6">
              {info.adjustable
                ? "Prüfen Sie den Nettobetrag · Sie können ihn vor dem Versand anpassen."
                : "Ein Klick · danach geht die Note direkt an Ihre Kanzlei."}
            </p>
            <Row label="Note" testId="lex-approve-number">{info.invoice_number}</Row>
            <Row label="Empfänger" testId="lex-approve-recipient">{info.customer_name}</Row>
            {period && <Row label="Leistungszeitraum" testId="lex-approve-period">{period}</Row>}
            {info.adjustable ? (
              <div className="py-4 border-b border-slate-100">
                <label className="block text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2">
                  Nettobetrag (anpassbar)
                </label>
                <input type="number" min="0.01" step="0.01" className="lex-input lex-mono text-lg" value={amount}
                  onChange={(e) => setAmount(e.target.value)} data-testid="lex-approve-amount-input" />
              </div>
            ) : (
              <Row label="Nettobetrag" testId="lex-approve-net">{fmtEur(info.base_amount_net ?? info.amount_net)}</Row>
            )}
            <div className="py-4 border-b border-slate-100">
              <LexExtrasFields value={extras} onChange={setExtras} testPrefix="lex-approve-extras" />
            </div>
            <Row label="Gesamt brutto" testId="lex-approve-gross">{fmtEur(brutto)}</Row>
            {error && <p className="text-sm mt-4" style={{ color: "#991B1B" }} data-testid="lex-approve-error">{error}</p>}
            <button onClick={confirm} disabled={sending || (info.adjustable && !(parseFloat(amount) > 0))}
              className="lex-btn w-full mt-6" data-testid="lex-approve-confirm-btn">
              {sending ? "Wird versendet …" : "Freigeben & an Kanzlei senden"}
            </button>
            <p className="text-xs text-slate-400 mt-3 text-center">
              Nach dem Versand erhalten Sie eine Bestätigung mit der Note als PDF.
            </p>
          </div>
        ) : (
          <div className="lex-card p-8 text-center lex-fade-up" data-testid="lex-approve-notfound">
            <AlertTriangle className="h-8 w-8 mx-auto mb-4 text-slate-400" />
            <p className="text-sm text-slate-600">{String(error)}</p>
          </div>
        )}
      </div>
    </div>
  );
}
